/**
 * Room-related types shared between frontend and backend
 * Used for real-time room management with Ably
 */

/**
 * Game configuration constants shared between frontend and backend
 */
export const GAME_CONSTANTS = {
  MIN_PLAYERS: 2,
  MAX_PLAYERS: 4,
  MATCHMAKING_TIMEOUT_SECONDS: 60
} as const;

/**
 * Room status constants
 * Prefer using these over magic strings
 */
export const RoomStatus = {
  WAITING: 'waiting',
  STARTING: 'starting',
  ACTIVE: 'active',
  CLOSED: 'closed'
} as const;

export type RoomStatus = (typeof RoomStatus)[keyof typeof RoomStatus];

/**
 * Room game type constants
 */
export const RoomGameType = {
  FREE: 'free',
  PAID: 'paid'
} as const;

export type RoomGameType = (typeof RoomGameType)[keyof typeof RoomGameType];

export interface Room {
  code: string; // 6-character alphanumeric code
  creatorId: string; // User who created the room
  gameType: RoomGameType; // Game mode (free or paid)
  playerCount: number; // Current number of players (1-4)
  maxPlayers: number; // Always 4 for LUDOKA
  status: RoomStatus; // Current room status
  createdAt: string; // ISO timestamp
  expiresAt: string | null; // Null when players present, ISO timestamp when empty
  channelName: string; // Ably channel name: game:lobby:${code}
  isMatchmaking?: boolean; // True for matchmaking rooms, undefined/false for private rooms
  matchmakingStartedAt?: string | null; // ISO timestamp when 2nd player joined (matchmaking only)
}

export interface PlayerSkinPreferences {
  pawn?: string;
  dice?: string;
  base?: string;
  plateaux?: string;
}

export interface PlayerPresence {
  userId: string; // Unique user identifier
  username?: string; // Optional username for display
  joinedAt: number; // Unix timestamp in milliseconds
  status: 'connected' | 'ready';
  skinPreferences?: PlayerSkinPreferences; // Per-component skin choices
  isBot?: boolean; // True for bot players in real Ably presence (story 04-06)
}

export interface RoomCreateRequest {
  gameType: RoomGameType; // Type of game (free or paid)
}

export interface RoomCreateResponse {
  room: Room; // Created room details
}

export interface RoomJoinRequest {
  code: string; // 6-character room code to join
}

export interface RoomJoinResponse {
  room: Room; // Joined room details
}

export interface RoomStateResponse {
  room: Room;
  presenceMembers: PlayerPresence[];
}

// Ably Events
export interface GameStartingEvent {
  countdownSeconds: number; // Countdown duration (default 10)
  startedAt: number; // Unix timestamp when countdown started
}

export interface GameStartEvent {
  gameId: string; // Game ID to navigate to
  players: string[]; // Array of player userIds
}

export interface MatchmakingTimerStartedEvent {
  startedAt: string; // ISO timestamp when timer started
  timeoutSeconds: number; // Timeout duration in seconds
}

export interface MatchmakingTimerResetEvent {
  reason: 'not_enough_players'; // Why the timer was reset
}

// Published on game:lobby:${code} when a paid match fails to start because SOKA
// refused to record it (Story 01-04). The match does not begin.
export interface MatchStartFailedEvent {
  reason: string; // SOKA rejection code (e.g. 'GHOST_PLAYER')
  retryable: boolean; // Whether the user can simply retry
}

// Matchmaking types
export interface MatchmakingRequest {
  gameType: RoomGameType;
}

export interface MatchmakingResponse {
  room: Room;
  isCreator: boolean; // True if user created new room, false if joined existing
}

export interface UpdatePresenceResponse {
  room: Room;
}

export interface WaitingCountsResponse {
  free: number;
  paid: number;
}
